import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import cx from "classnames";
import { convertDatesToTimezone } from "services/report.service";
import { CSVLink } from "react-csv";
import dateformat from "dateformat";
import { Button } from "react-bootstrap";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";

import { TableFooterInfo, TableHeaderInfo } from "..";
import { EmptyMessage, LeadAnalyticsHeader, LoadingMessage, VisitorProfileView } from "../../reports/components";
import { fetchingWorkspaceLeadReports, fetchLeadReportsForDownload, invalidateLeadAnalyticsDownloadData } from "actions/workspaceReports";
import { leadReportHeader } from "constants/workspaceReportsData";
import { StoryboardDetails } from ".";
import { useParams } from "react-router-dom";
import { formatDurationWithLabel } from "../../reports/reporthelpers";
import { renameTheLabel } from "pages/pages/preview/icV2/helper";
import styles from '../workspaceReports.module.scss'

export default function WorkspaceLeadsReportsAnalytics({ date, contentType, modalView = "", sectionId = "" }) {
  const dispatch = useDispatch();

  const [currentChart, setCurrentChart] = useState("table_view");
  const loading = useSelector(state => _.get(state, `workspaces.reports.leads.loading`, false));
  const data = useSelector(state => _.get(state, `workspaces.reports.leads.items`, []));
  const pagination = useSelector(state => _.get(state, 'workspaces.reports.leads.pagination', {}));
  const totalCount = pagination.totalRecordsCount || 0;

  useEffect(() => {
    if (date || (contentType && contentType.length > 0)) {
      handlePageChange(0);
    }
  }, [date, contentType]);

  const handlePageChange = (page) => {
    handleLeadReports(page);
  }

  const handleLeadReports = (page) => {
    if (pagination.current != -1) {
      const request = {
        types: contentType,
        ...convertDatesToTimezone(date),
      }

      dispatch(fetchingWorkspaceLeadReports(request, page, 20));
    }
  }

  useEffect(() => {
    if (pagination.current == -1) {
      const request = {
        types: contentType,
        ...convertDatesToTimezone(date),
      }
      dispatch(fetchingWorkspaceLeadReports(request, 0, 200));
    }
  }, [pagination]);

  return (
    <div className={cx(styles.storyboardReportsWrapper, { [styles.modalWrapper]: modalView })} id={sectionId}>
      <div className={cx(styles.storyboardReportsContainer)}>
        <TableHeaderInfo title="Lead Analytics" count={pagination.totalRecordsCount || 0} modalView={modalView} />
        <>
          <WorkspaceLeadsReports
            data={data}
            modalView={modalView}
            date={date}
            count={totalCount}
            type={contentType}
            loading={loading}
            contentType={contentType}
          />
        </>

        {/* {currentChart == "graph_view" &&
          <div className={styles.graphView}>Graph view</div>
        } */}
      </div>
      <TableFooterInfo
        data={data}
        currentChart={currentChart}
        disableView={['pie_chart_view']}
        modalView={modalView}
        onChange={handlePageChange}
        type='leadReports'
        pagination={pagination}
        paginationName={"Leads"}
        date={date}
        contentType={contentType}
      />
    </div>
  )
}

export const WorkspaceLeadsReports = ({ modalView, data, date, type, loading }) => {
  const leadsData = useSelector((state) => {
    const records = Array.isArray(data) ? data : [];
    return modalView ? records : records.slice(0, 5);
  });
  let { storyboardId } = useParams();

  const updatedLeadReportHeader = useMemo(() => {
    leadsData.forEach((lead) => {
      lead.fields.forEach((field) => {
        const exists = leadReportHeader.some((header) => header.field === field.field);
        // if (!exists && field && field.uid == null) {
        //   leadReportHeader.push({
        //     name: field.name,
        //     field: field.field,
        //     uid: field.uid,
        //   });
        // }
      });
    });
    return leadReportHeader;
  }, [leadsData]);


  const renderFieldData = (leadData, headerField) => {
    switch (true) {
      case headerField.uid === 'createdDate':
        const createdDdtae = dateformat(leadData.contact.createdDate, 'dd mmm yyyy');
        return createdDdtae;

      case headerField.uid === 'lastVisitedDate':
        return dateformat(leadData.lastVisitedDate, 'dd mmm yyyy');

      case headerField.uid === 'timeSpent':
        return formatDurationWithLabel(leadData[headerField.uid]).value;

      case headerField.uid === 'completion':
        return `${leadData[headerField.uid]}%`;

      default:
        if (headerField.uid != null) {
          if (leadData[headerField.uid] !== undefined) {
            return leadData[headerField.uid] || "-";
          }
          if (leadData.contact && leadData.contact[headerField.uid] !== undefined) {
            return leadData.contact[headerField.uid] || "-";
          }
          return "";
        } else {
          const customField = Array.isArray(leadData?.fields) ? leadData.fields.find((field) => field.field === headerField.field) : null;
          if (customField) {
            if (typeof customField.values === 'boolean') {
              return customField.values.toString() || "-";
            }
            if (Array.isArray(customField.values)) {
              return customField.values.length > 0 ? customField.values.join(', ') : "-";
            }
            return customField.values || "-";
          }
          return "-";
        }
    }
  };

  return (
    <div className={cx(styles.storyboardReportsView, styles.leadReportContainer, { [styles.tableDataOverflow]: modalView })}>
      <div className={cx(styles.tableHeaderWrapper, { [styles.sticky]: modalView })}>
        <div className={styles.downloadOption}>
          <ExportCSV date={date} type={type} />
        </div>
      </div>
      {leadsData.length == 0 && <LeadAnalyticsHeader header={updatedLeadReportHeader} storyboardId={storyboardId} isWorkspace={true} />}
      <div className={styles.tableOverflow}>
        <table className={cx(styles.leadsAnalyticsTable)}>
          {loading &&
            <div className={cx(styles.loader, { [styles.modalLoader]: modalView })}>
              <LoadingMessage className={styles.loadingMessage} />
            </div>
          }
          {!loading && data.length == 0 &&
            <div className={cx(styles.loader, { [styles.modalLoader]: modalView })}>
              <EmptyMessage />
            </div>
          }

          {!loading && data && data.length > 0 &&
            <>
              <thead className={cx(styles.tableHeader, { [styles.stickyHeader]: modalView })}>
                <tr className={styles.headerRow}>
                  {updatedLeadReportHeader.map((column, key) => {
                    return (
                      <th key={key} className={cx(styles.headerData, { [styles.stickyColumn]: key === 0 })}>
                        {column.name}
                      </th>
                    )
                  })}
                </tr>
              </thead>
            
              <tbody className={styles.tableBody}>
                {leadsData.map((lead, rowIndex) => (
                  <tr key={rowIndex} className={styles.tableBodyDataRow}>
                    {updatedLeadReportHeader.map((field, index) => (
                      <td key={index} className={cx(styles.tableBodyData, { [styles.stickyColumn]: index === 0 })}>
                        {field.uid === "firstName" ? (
                          <VisitorProfileView
                            data={lead}
                            reportDate={date}
                            showProfile={false}
                            type="leads"
                          />
                        ) : field.uid === "storyboard" ? (
                          <StoryboardDetails data={lead} />
                        ) : (
                          <TruncatedTextWithTooltip
                            content={renderFieldData(lead, field)}
                            maxWidth={250}
                            placement="top"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </>
          }
        </table>
      </div>
    </div>
  );
};


function ExportCSV({ date, type }) {
  const dispatch = useDispatch();

  const downloads = useSelector(state => _.get(state, 'workspaces.download.leads.items', []));
  const completed = useSelector(state => _.get(state, 'workspaces.download.leads.completed'));
  const progress = useSelector(state => _.get(state, 'workspaces.download.leads.progress'));
  const pagination = useSelector(state => _.get(state, 'workspaces.download.leads.pagination', {}));
  const [csvData, setCsvData] = useState([]);
  const csvLink = useRef();

  const headers = [
    { label: "First Name", key: "firstName" },
    { label: "Last Name", key: "lastName" },
    { label: "Email Id", key: "email" },
    { label: "Company", key: "company" },
    { label: "Title", key: "title" },
    { label: "Work Phone", key: "workPhone" },
    { label: "Storyboard", key: "storyboard" },
    { label: "Storyboard Type", key: "contentType"},
    { label: "Source", key: "source" },
    { label: "Medium", key: "medium" },
    { label: "Campaign", key: "campaign" },
    { label: "Created Date", key: "createdDate" },
    { label: "Last Visited", key: "lastVisitedDate" },
    { label: "Views", key: "Views" },
    { label: "Time Spent", key: "timeSpent" },
    { label: "Completion %", key: "completion" },
  ];

  useEffect(() => {
    if (completed) {
      var columns = [];
      downloads && downloads.map((data, index) => {

        columns[index] = {
          firstName: data.contact.firstName || "-",
          lastName: data.contact.lastName || "-",
          email: data.contact.email || "-",
          company: data.contact.company || "-",
          title: data.contact.title || "-",
          workPhone: data.contact.mobilePhone || "-",
          storyboard: data.storyboardResponse.name || "-",
          contentType: renameTheLabel(data.storyboardResponse.type) || "_",
          source: data.campaignSource || "-",
          medium: data.campaignMedium || "-",
          campaign: data.campaignName || 0,
          createdDate: data.contact.createdDate == undefined ? "" : dateformat(data.contact.createdDate, 'dd mmm yyyy'),
          lastVisitedDate: data.lastVisitedDate == undefined ? "" : dateformat(data.lastVisitedDate, 'dd mmm yyyy'),
          Views: data.views || 0,
          timeSpent: formatDurationWithLabel(data.timeSpent).value || 0,
          completion: data.completion + "%" || 0,
        }
      });

      setCsvData(columns);
    }
  }, [completed]);

  useEffect(() => {
    if (!completed && csvData && csvData.length == 0) {
      return;
    }

    csvLink.current.link.click();
    dispatch(invalidateLeadAnalyticsDownloadData());
  }, [csvData]);

  const fetchLeadReportData = () => {
    dispatch(invalidateLeadAnalyticsDownloadData());

    const request = {
      types: type,
      ...convertDatesToTimezone(date),
    }
    dispatch(fetchLeadReportsForDownload(request, 0, 200))

  }

  return (
    <div>
      <Button variant="transparent" disabled={progress} onClick={() => fetchLeadReportData()} className={styles.download}>
        <i className={cx("cleverstory-download", styles.downloadIcon)} />
        <span className={styles.progress}>{progress ? "Downloading..." : "Download "}</span>
      </Button>
      <CSVLink
        ref={csvLink}
        headers={headers}
        data={csvData}
        filename='leadReports.csv'
        className='hidden'
      />
    </div>
  )
}